import React from 'react';

import { faCheck, faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import car from 'src/assets/images/car.png';
import { badgePayStationStateOptions } from 'src/features/cards/utils/constants';
import { useListPayStationHeartBeats } from 'src/features/payStations/api/useListPayStationHeartbeats';
import { CURRENCIES, LANGUAGES, type PayStationDevice } from 'src/features/payStations/types';
import payStationsMessages from 'src/features/payStations/utils/messages';
import {
    GeneralBlock,
    GeneralBlockBody,
    GeneralBlockHeader,
    GeneralBlockTitle,
    GeneralBlockValue,
} from 'src/registry/components/blocks/GeneralBlock';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { Badge } from 'src/registry/ui/Badge';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { useTitleStore } from 'src/stores/useTitleStore';
import { storage } from 'src/utils/storage.ts';

interface InfoDashboardProps {
    payStationData: PayStationDevice | undefined;
    isLoading?: boolean;
}

const InformationDashboard = ({ payStationData, isLoading = true }: InfoDashboardProps) => {
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();

    const { data: lastHeartbeat } = useListPayStationHeartBeats({
        payStationId: payStationData?.id || '',
        config: { enabled: !!payStationData },
        limit: 1,
        ordering: '-timestamp',
    });

    const lastHeartbeatTime = React.useMemo(() => {
        if (!lastHeartbeat || !lastHeartbeat.results.length) {
            return null;
        }

        const value = lastHeartbeat.results[0].timestamp;
        const locale = storage.getLanguage();
        if (value == null) return null;

        const datetime = new Date(value.toString());
        const formattedDate = datetime ? new Date(datetime.toString()).toLocaleDateString(locale) : '';
        const formattedTime = datetime ? new Date(datetime.toString()).toLocaleTimeString(locale) : '';

        return `${formattedTime} ${formattedDate}`;
    }, [lastHeartbeat]);

    React.useEffect(() => {
        if (!payStationData) return;
        setTitle(payStationData.identifier);
        return () => {
            removeTitle();
        };
    }, [payStationData]);

    const option = React.useMemo(() => {
        return badgePayStationStateOptions[payStationData?.state as string];
    }, [payStationData]);

    const GeneralBlockWrapper = React.useCallback(
        ({ header, children }: { header: string; children: React.ReactNode }) => {
            return (
                <div className="flex flex-col md:gap-1">
                    <GeneralBlockHeader variant="dashboard">
                        <GeneralBlockTitle>{header}</GeneralBlockTitle>
                    </GeneralBlockHeader>
                    <GeneralBlockBody variant="dashboard">{children}</GeneralBlockBody>
                </div>
            );
        },
        [isLoading],
    );

    const GeneralBlockContainer = React.useCallback(
        ({ header, description }: { header: string; description?: string | null }) => {
            return (
                <div className="flex flex-col md:gap-1">
                    <GeneralBlockHeader variant="dashboard">
                        <GeneralBlockTitle>{header}</GeneralBlockTitle>
                    </GeneralBlockHeader>
                    <GeneralBlockBody variant="dashboard">
                        {isLoading ? (
                            <Skeleton className="h-8 w-full max-w-36" />
                        ) : (
                            <GeneralBlockValue size="small">{description == '' ? '-' : description}</GeneralBlockValue>
                        )}
                    </GeneralBlockBody>
                </div>
            );
        },
        [isLoading],
    );

    const informationDetails = React.useMemo(() => {
        const settings = payStationData?.settings;

        return [
            {
                header: formatMessage(payStationsMessages.serialNumber),
                description: payStationData?.serial_number,
            },
            {
                header: formatMessage(payStationsMessages.version),
                description: payStationData?.app_version,
            },
            {
                header: formatMessage(payStationsMessages.pushVersion),
                description: payStationData?.app_push_update_version,
            },
            {
                header: formatMessage(payStationsMessages.parking),
                description: payStationData?.parking?.name,
            },
            {
                header: formatMessage(payStationsMessages.lastHeartBeat),
                description: lastHeartbeatTime,
            },
            {
                header: formatMessage(payStationsMessages.languages),
                description:
                    settings?.languages &&
                    settings?.languages.map((language) => formatMessage(payStationsMessages[language])).join(', '),
            },
            {
                header: formatMessage(payStationsMessages.systemPassword),
                description: settings?.password,
            },
            {
                header: formatMessage(payStationsMessages.servicesPassword),
                description: settings?.servicesPassword,
            },
            {
                header: formatMessage(payStationsMessages.imagesURL),
                description: settings?.imagesURL,
            },
            {
                header: formatMessage(payStationsMessages.receiptsURL),
                description: settings?.receiptURL,
            },
            {
                header: formatMessage(payStationsMessages.serverHost),
                description: settings?.serverHost,
            },
            {
                header: formatMessage(payStationsMessages.serverPort),
                description: settings?.serverPort,
            },
            {
                header: formatMessage(payStationsMessages.homeQRValue),
                description: settings?.homeQRValue,
            },
            {
                header: formatMessage(payStationsMessages.imagesPrefix),
                description: settings?.imagePrefix,
            },
            {
                header: formatMessage(payStationsMessages.allowedPaymentMethods),
                description:
                    settings?.paymentMethods &&
                    settings?.paymentMethods.map((method) => formatMessage(payStationsMessages[method])).join(', '),
            },
            {
                header: formatMessage(payStationsMessages.acceptedCoins, { currency: CURRENCIES.BGN }),
                description: settings?.acceptedCoins && settings?.acceptedCoins[CURRENCIES.BGN].split(',').join(', '),
            },
            {
                header: formatMessage(payStationsMessages.acceptedCoins, { currency: CURRENCIES.EUR }),
                description: settings?.acceptedCoins && settings?.acceptedCoins[CURRENCIES.EUR].split(',').join(', '),
            },
            {
                header: formatMessage(payStationsMessages.acceptedBanknotes, { currency: CURRENCIES.BGN }),
                description:
                    settings?.acceptedBanknotes && settings?.acceptedBanknotes[CURRENCIES.BGN].split(',').join(', '),
            },
            {
                header: formatMessage(payStationsMessages.acceptedBanknotes, { currency: CURRENCIES.EUR }),
                description:
                    settings?.acceptedBanknotes && settings?.acceptedBanknotes[CURRENCIES.EUR].split(',').join(', '),
            },
            {
                header: formatMessage(payStationsMessages.returnCoins, { currency: CURRENCIES.BGN }),
                description: settings?.coinsToReturn && settings?.coinsToReturn[CURRENCIES.BGN].split(',').join(', '),
            },
            {
                header: formatMessage(payStationsMessages.returnCoins, { currency: CURRENCIES.EUR }),
                description: settings?.coinsToReturn && settings?.coinsToReturn[CURRENCIES.EUR].split(',').join(', '),
            },
            {
                header: formatMessage(payStationsMessages.returnBanknotes, { currency: CURRENCIES.BGN }),
                description:
                    settings?.banknotesToReturn && settings?.banknotesToReturn[CURRENCIES.BGN].split(',').join(', '),
            },
            {
                header: formatMessage(payStationsMessages.returnBanknotes, { currency: CURRENCIES.EUR }),
                description:
                    settings?.banknotesToReturn && settings?.banknotesToReturn[CURRENCIES.EUR].split(',').join(', '),
            },
            {
                header: formatMessage(payStationsMessages.supportPhone),
                description: settings?.supportPhone,
            },
            {
                header: formatMessage(payStationsMessages.homeButtonPayText, {
                    language: formatMessage(payStationsMessages[LANGUAGES.BULGARIAN]),
                }),
                description: settings?.homePayButtonText && settings?.homePayButtonText[LANGUAGES.BULGARIAN],
            },
            {
                header: formatMessage(payStationsMessages.homeButtonPayText, {
                    language: formatMessage(payStationsMessages[LANGUAGES.ENGLISH]),
                }),
                description: settings?.homePayButtonText && settings?.homePayButtonText[LANGUAGES.ENGLISH],
            },
            {
                header: formatMessage(payStationsMessages.webId),
                description: settings?.webId,
            },
            {
                header: formatMessage(payStationsMessages.defaultLanguage),
                description: payStationData?.settings?.defaultLanguage
                    ? formatMessage(payStationsMessages[payStationData.settings?.defaultLanguage])
                    : null,
            },
        ];
    }, [payStationData, lastHeartbeatTime]);
    return (
        <div className="flex flex-col gap-4 overflow-hidden sm:gap-6 md:flex-row">
            <SectionBlock className="overflow-hidden">
                <SectionBlockHeader className="min-h-9">
                    <SectionBlockHead>
                        <SectionBlockTitle>{formatMessage(payStationsMessages.deviceInfo)}</SectionBlockTitle>
                    </SectionBlockHead>
                </SectionBlockHeader>
                <SectionBlockSeparator />
                <GeneralBlock asChild>
                    <SectionBlockBody className="xs:grid-cols-2 grid md:grid-cols-1 lg:grid-cols-2">
                        <GeneralBlockWrapper header={formatMessage(payStationsMessages.state)}>
                            {isLoading ? (
                                <Skeleton className="h-8 w-full max-w-52" />
                            ) : (
                                <GeneralBlockValue size="small">
                                    <Badge theme={option.theme} className="text-base" style={option.color}>
                                        {formatMessage(option.label)}
                                    </Badge>
                                </GeneralBlockValue>
                            )}
                        </GeneralBlockWrapper>
                        {informationDetails.map(({ header, description }) => (
                            <GeneralBlockContainer header={header} description={description} key={header} />
                        ))}
                        <GeneralBlockWrapper header={formatMessage(payStationsMessages.fiscalDeviceWithPaper)}>
                            {isLoading ? (
                                <Skeleton className="h-8 w-full max-w-52" />
                            ) : (
                                <GeneralBlockValue size="small">
                                    <FontAwesomeIcon
                                        icon={payStationData?.settings?.fiscalDevicePaper ? faCheck : faX}
                                    />
                                </GeneralBlockValue>
                            )}
                        </GeneralBlockWrapper>
                        <GeneralBlockWrapper header={formatMessage(payStationsMessages.reportPaperInLength)}>
                            {isLoading ? (
                                <Skeleton className="h-8 w-full max-w-52" />
                            ) : (
                                <GeneralBlockValue size="small">
                                    <FontAwesomeIcon
                                        icon={payStationData?.settings?.reportPaperInLength ? faCheck : faX}
                                    />
                                </GeneralBlockValue>
                            )}
                        </GeneralBlockWrapper>
                        <GeneralBlockWrapper header={formatMessage(payStationsMessages.lostTicket)}>
                            {isLoading ? (
                                <Skeleton className="h-8 w-full max-w-52" />
                            ) : (
                                <GeneralBlockValue size="small">
                                    <FontAwesomeIcon icon={payStationData?.settings?.lostTicket ? faCheck : faX} />
                                </GeneralBlockValue>
                            )}
                        </GeneralBlockWrapper>
                        <GeneralBlockWrapper header={formatMessage(payStationsMessages.enableSettingsEnter)}>
                            {isLoading ? (
                                <Skeleton className="h-8 w-full max-w-52" />
                            ) : (
                                <GeneralBlockValue size="small">
                                    <FontAwesomeIcon
                                        icon={payStationData?.settings?.enableSettingsEnter ? faCheck : faX}
                                    />
                                </GeneralBlockValue>
                            )}
                        </GeneralBlockWrapper>
                        <GeneralBlockWrapper header={formatMessage(payStationsMessages.enableSubscriptions)}>
                            {isLoading ? (
                                <Skeleton className="h-8 w-full max-w-52" />
                            ) : (
                                <GeneralBlockValue size="small">
                                    <FontAwesomeIcon
                                        icon={payStationData?.settings?.enableSubscriptions ? faCheck : faX}
                                    />
                                </GeneralBlockValue>
                            )}
                        </GeneralBlockWrapper>
                    </SectionBlockBody>
                </GeneralBlock>
                <img src={car} alt="Car" className="absolute -right-12 bottom-0 z-10 w-48 max-w-96 md:w-[20vw]" />
            </SectionBlock>
        </div>
    );
};
export { InformationDashboard };
